(function () {
  'use strict';

  if (window.OneLuxuryTabbedProductCarousel) return;
  window.OneLuxuryTabbedProductCarousel = true;

  var ROOT_NAME = 'one-luxury-tabbed-product-carousel';

  function deviceClass() {
    if (window.matchMedia('(max-width: 639px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 989px)').matches) return 'tablet';
    return 'desktop';
  }

  function publish(name, detail) {
    if (window.Shopify && window.Shopify.designMode) return;

    var payload = Object.assign({ device_class: deviceClass() }, detail || {});
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try {
        window.Shopify.analytics.publish(name, payload);
        return;
      } catch (error) {
        // Fall through to the theme's event-bus convention.
      }
    }
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));
  }

  function safeSessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return '';
    }
  }

  function safeSessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  class OneLuxuryTabbedProductCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[role="tabpanel"]'));
      this.sectionKey = this.getAttribute('data-section-key') || 'homepage-carousel';
      this.sectionId = this.getAttribute('data-section-id') || '';
      this.storageKey = 'oneLuxuryHomeCarousel:' + this.sectionKey;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.tabs.length || !this.panels.length) return;

      this.addEventListener('click', this.onClick.bind(this), { signal: this.signal });
      this.addEventListener('keydown', this.onKeydown.bind(this), { signal: this.signal });
      document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this), { signal: this.signal });

      this.panels.forEach(this.preparePanel.bind(this));

      var selected = this.tabs.find(function (tab) { return tab.getAttribute('aria-selected') === 'true'; });
      var savedKey = safeSessionGet(this.storageKey);
      var requested = savedKey ? this.tabForKey(savedKey) : null;
      if (requested && requested.getAttribute('aria-disabled') === 'true') requested = null;

      if (!requested && this.getAttribute('data-default-behavior') === 'current_month') {
        var month = String(new Date().getMonth() + 1);
        requested = this.tabs.find(function (tab) {
          return tab.getAttribute('data-month-number') === month && tab.getAttribute('aria-disabled') !== 'true';
        });
      }

      if (requested && requested !== selected) {
        this.activate(requested, { emit: false, focus: false, persist: false });
      } else if (selected) {
        this.ensurePanel(this.panelForTab(selected));
        this.updateControls(this.panelForTab(selected));
      }
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.querySelectorAll('[data-home-carousel-track]').forEach(function (track) {
        if (track._oneLuxuryDepthTimer) window.clearTimeout(track._oneLuxuryDepthTimer);
      });
      this.controller = null;
    }

    tabForKey(key) {
      return this.tabs.find(function (tab) { return tab.getAttribute('data-tab-key') === key; });
    }

    panelForTab(tab) {
      if (!tab) return null;
      var id = tab.getAttribute('aria-controls');
      return id ? this.querySelector('#' + window.CSS.escape(id)) : null;
    }

    activeTab() {
      return this.tabs.find(function (tab) { return tab.getAttribute('aria-selected') === 'true'; });
    }

    activePanel() {
      return this.panelForTab(this.activeTab());
    }

    preparePanel(panel) {
      var viewport = panel.querySelector('[data-home-carousel-viewport]');
      if (!viewport) return;
      viewport.addEventListener('scroll', this.onScroll.bind(this, panel), { passive: true, signal: this.signal });
      window.addEventListener('resize', this.updateControls.bind(this, panel), { passive: true, signal: this.signal });
    }

    ensurePanel(panel) {
      if (!panel) return;
      var track = panel.querySelector('[data-home-carousel-track]');
      var template = panel.querySelector('template[data-home-carousel-template]');
      if (track && template) {
        track.appendChild(template.content.cloneNode(true));
        template.remove();
        track.setAttribute('data-loaded', 'true');
      }
      this.updateControls(panel);
    }

    activate(tab, options) {
      options = options || {};
      if (!tab || tab.getAttribute('aria-disabled') === 'true') return;

      var previous = this.activeTab();
      var previousKey = previous ? previous.getAttribute('data-tab-key') : '';
      var nextKey = tab.getAttribute('data-tab-key') || '';
      if (previous === tab) return;

      this.tabs.forEach(function (candidate) {
        var active = candidate === tab;
        candidate.setAttribute('aria-selected', active ? 'true' : 'false');
        candidate.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.panels.forEach(function (panel) {
        panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      });

      var panel = this.panelForTab(tab);
      this.ensurePanel(panel);

      if (options.focus) tab.focus();
      var tablist = tab.closest('[role="tablist"]');
      if (tablist) {
        var desiredLeft = tab.offsetLeft - ((tablist.clientWidth - tab.offsetWidth) / 2);
        tablist.scrollTo({ left: Math.max(0, desiredLeft), behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      }

      if (options.persist !== false) safeSessionSet(this.storageKey, nextKey);
      if (options.emit !== false) {
        publish('homepage_carousel_tab_select', {
          section_key: this.sectionKey,
          section_id: this.sectionId,
          tab_key: nextKey,
          tab_label: tab.getAttribute('data-tab-label') || tab.textContent.trim(),
          previous_tab: previousKey
        });
      }
    }

    onClick(event) {
      var tab = event.target.closest('[role="tab"]');
      if (tab && this.contains(tab)) {
        this.activate(tab, { emit: true, focus: false, persist: true });
        return;
      }

      var directionButton = event.target.closest('[data-home-carousel-direction]');
      if (directionButton && this.contains(directionButton)) {
        var panel = directionButton.closest('[role="tabpanel"]');
        var viewport = panel && panel.querySelector('[data-home-carousel-viewport]');
        if (!viewport) return;
        var direction = directionButton.getAttribute('data-home-carousel-direction') === 'next' ? 1 : -1;
        viewport.scrollBy({
          left: direction * Math.max(280, viewport.clientWidth * 0.9),
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
        });
        return;
      }

      var target = event.target.closest('a');
      if (!target || !this.contains(target)) return;

      var panel = target.closest('[role="tabpanel"]') || this.activePanel();
      var tabKey = panel ? panel.getAttribute('data-tab-key') || '' : '';
      var card = target.closest('[data-home-carousel-card]');

      if (target.hasAttribute('data-home-carousel-product-link') && card) {
        publish('homepage_carousel_product_click', {
          section_key: this.sectionKey,
          tab_key: tabKey,
          product_id: card.getAttribute('data-product-id') || '',
          product_handle: card.getAttribute('data-product-handle') || '',
          product_position: Number(card.getAttribute('data-product-position') || 0),
          displayed_price: Number(card.getAttribute('data-displayed-price') || 0) / 100
        });
      } else if (target.hasAttribute('data-home-carousel-choose-options') && card) {
        publish('homepage_carousel_choose_options', {
          section_key: this.sectionKey,
          tab_key: tabKey,
          product_id: card.getAttribute('data-product-id') || '',
          product_handle: card.getAttribute('data-product-handle') || ''
        });
      } else if (target.hasAttribute('data-home-carousel-view-all')) {
        publish('homepage_carousel_view_all', {
          section_key: this.sectionKey,
          tab_key: tabKey,
          destination: target.getAttribute('href') || ''
        });
      } else if (target.hasAttribute('data-home-carousel-quote')) {
        publish('homepage_carousel_quote_click', {
          section_key: this.sectionKey,
          tab_key: tabKey,
          quote_type: target.getAttribute('data-quote-type') || 'custom',
          destination: target.getAttribute('href') || ''
        });
      }
    }

    onKeydown(event) {
      var tab = event.target.closest('[role="tab"]');
      if (!tab || !this.contains(tab)) return;
      var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (keys.indexOf(event.key) === -1) return;

      event.preventDefault();
      var enabled = this.tabs.filter(function (candidate) {
        return candidate.getAttribute('aria-disabled') !== 'true';
      });
      var index = enabled.indexOf(tab);
      if (index < 0) return;

      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = enabled.length - 1;
      else if (event.key === 'ArrowRight') index = (index + 1) % enabled.length;
      else index = (index - 1 + enabled.length) % enabled.length;

      this.activate(enabled[index], { emit: true, focus: true, persist: true });
    }

    onBlockSelect(event) {
      var block = event.target && event.target.closest ? event.target.closest('[data-tab-key]') : null;
      if (!block || !this.contains(block)) return;
      var tab = this.tabForKey(block.getAttribute('data-tab-key'));
      if (tab) this.activate(tab, { emit: false, focus: false, persist: false });
    }

    onScroll(panel) {
      this.updateControls(panel);
      var track = panel.querySelector('[data-home-carousel-track]');
      if (!track) return;
      if (track._oneLuxuryDepthTimer) window.clearTimeout(track._oneLuxuryDepthTimer);
      track._oneLuxuryDepthTimer = window.setTimeout(this.reportDepth.bind(this, panel), 180);
    }

    updateControls(panel) {
      if (!panel || panel.hidden) return;
      var viewport = panel.querySelector('[data-home-carousel-viewport]');
      if (!viewport) return;
      var maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      var previous = panel.querySelector('[data-home-carousel-direction="previous"]');
      var next = panel.querySelector('[data-home-carousel-direction="next"]');
      var controls = panel.querySelector('.one-luxury-tabbed-product-carousel__controls');
      if (controls) controls.hidden = maxScroll <= 2;
      if (previous) previous.disabled = viewport.scrollLeft <= 2;
      if (next) next.disabled = viewport.scrollLeft >= maxScroll - 2 || maxScroll <= 2;
    }

    reportDepth(panel) {
      var viewport = panel.querySelector('[data-home-carousel-viewport]');
      var cards = Array.prototype.slice.call(panel.querySelectorAll('[data-home-carousel-card]'));
      if (!viewport || !cards.length) return;
      var visibleEdge = viewport.scrollLeft + viewport.clientWidth;
      var maximum = cards.reduce(function (max, card, index) {
        return card.offsetLeft < visibleEdge ? index + 1 : max;
      }, 1);
      var reported = Number(panel.getAttribute('data-maximum-position-reported') || 0);
      if (maximum <= reported) return;
      panel.setAttribute('data-maximum-position-reported', String(maximum));
      publish('homepage_carousel_swipe_depth', {
        section_key: this.sectionKey,
        tab_key: panel.getAttribute('data-tab-key') || '',
        maximum_card_position_seen: maximum
      });
    }
  }

  customElements.define(ROOT_NAME, OneLuxuryTabbedProductCarousel);
})();
